/**
 * WebSocket message protocol — replaces Socket.IO event system.
 *
 * All messages on the wire are JSON envelopes of the shape:
 *
 *   { event: string, data?: unknown, ackId?: string }
 *
 * The optional `ackId` enables request/response pairs (replaces Socket.IO
 * acknowledgement callbacks). The receiver of a message with an `ackId`
 * must reply with a message carrying the same `ackId` and the result
 * (or an error) in `data`.
 */

export type WsEnvelope<E extends string = string, D = unknown> = {
  event: E;
  data?: D;
  /** Request/response correlation id (Socket.IO ack replacement). */
  ackId?: string;
};

export type WsAckResponse<D = unknown> = {
  event: "__ack";
  ackId: string;
  data?: D;
  error?: string;
};

/** Internal events used for the protocol itself. */
export const WS_INTERNAL_EVENTS = {
  ACK: "__ack",
  PING: "__ping",
  PONG: "__pong",
} as const;

/** Helper: serialize an envelope for sending over the wire. */
export const encodeWsMessage = <E extends string, D>(env: WsEnvelope<E, D> | WsAckResponse<D>): string => {
  return JSON.stringify(env);
};

/** Helper: parse an incoming wire message. Returns null on invalid JSON. */
export const decodeWsMessage = <E extends string = string, D = unknown>(
  raw: string,
): WsEnvelope<E, D> | WsAckResponse<D> | null => {
  try {
    const parsed = JSON.parse(raw);
    if (parsed == null || typeof parsed !== "object" || typeof parsed.event !== "string") return null;
    return parsed as WsEnvelope<E, D> | WsAckResponse<D>;
  } catch {
    return null;
  }
};
